import React, { FunctionComponent, useEffect, useState } from 'react'
import { ApiCustomer } from '@vend/types'
import { PAYMENT_TYPES } from '@vend/business-components'
import { trackUserEvent } from '@vend/utilities'
import { useT } from '@transifex/react'
import { CustomerBadge } from '@vend/react-utilities'
import { useDispatch } from 'react-redux'
import { useAppSelector } from '../../../state/hooks'
import {
  customerAddedOnConfirmation,
  selectCustomer,
  selectSaleLocalId,
  selectSalePayments,
} from '../../../state/slices/currentSale/currentSaleSlice'
import { useCustomerManager, useSaleManager } from '../../../ng-compat/hooks'
import { isValidCustomer } from '../../../functions/sale'
import { CustomerSearch } from '../../customer/customer-search'
import { UpdateSaleCustomerModal } from '../../../modals/update-customer-modal'
import { getFeatureFromCache } from '../../../util/feature-cache'
import { PartialCustomerGroup } from '../../react/modals/common/types'
import { CustomerBalancesSection } from '../payment-panel/CustomerBalancesSection'
import { $rootScope } from '../../../ng-compat/imports'
import { EVENT } from '../../../constants'
import { WEB_REGISTER } from '../../web-register.constant'
import { EmailReceiptToggle } from './EmailReceiptToggle'
import { AddEmailToCustomer } from './AddEmailToCustomer'
import { RepeatPaymentMethodSave } from './RepeatPaymentMethodSave'
import { TerminalCardSave } from './TerminalCardSave/TerminalCardSave'
import { useTerminalCardSaveEligible } from './TerminalCardSave/useTerminalCardSaveEligible'

export const ConfirmationCustomer: FunctionComponent = () => {
  const t = useT()
  const dispatch = useDispatch()
  const customer = useAppSelector(selectCustomer)
  const saleId = useAppSelector(selectSaleLocalId)
  const salePayments = useAppSelector(selectSalePayments)
  const saleManager = useSaleManager()
  const customerManager = useCustomerManager()
  const shouldShowCustomerGroup = getFeatureFromCache('customer_groups')
  const [customerGroup, setCustomerGroup] = useState<
    PartialCustomerGroup | undefined
  >(undefined)
  const [currentQuery, setCurrentQuery] = useState<string>('')
  const [openAddCustomerModal, setOpenAddCustomerModal] = useState(false)
  const isTerminalCardSaveEligible = useTerminalCardSaveEligible()

  const addCustomerToSale = (customer: ApiCustomer) => {
    Promise.resolve(saleManager.addCustomer(customer))
      .then(() => {
        const lspayPaymentId = salePayments.find(
          payment =>
            payment.paymentType.type_id === PAYMENT_TYPES.LSPAY && payment.id
        )?.id

        trackUserEvent(WEB_REGISTER.DISPLAY_NAME, 'Customer Added to Sale', {
          customerId: customer.id,
          saleId,
          ...(lspayPaymentId ? { paymentId: lspayPaymentId } : {}),
          paymentStatus: 'after_payment',
        })
      })
      .catch(() => undefined)
    dispatch(customerAddedOnConfirmation())
    saleManager.saveSale()
  }

  useEffect(() => {
    // This catches the case where a customer is added in the customer modal
    // and marks it updated so the sale change will be persisted
    const remove = $rootScope.$on(EVENT.SALE_CUSTOMER_UPDATED, () => {
      dispatch(customerAddedOnConfirmation())
    })

    return remove
  }, [dispatch])

  useEffect(() => {
    if (!customer || !shouldShowCustomerGroup) {
      setCustomerGroup(undefined)
      return
    }

    customerManager.getCustomerGroup(customer).then(group => {
      setCustomerGroup(group || undefined)
    })
  }, [customer, customerManager, shouldShowCustomerGroup])

  if (isValidCustomer(customer)) {
    const hasEmail = Boolean(customer.email)

    return (
      <>
        <div className="vd-mt10 customer-card">
          <div className="wr-customer-badge">
            <div className="wr-customer-badge-content vd-pt4 vd-pb2 vd-pl4 vd-pr4">
              <CustomerBadge
                size="small"
                apiCustomer={customer}
                group={customerGroup}
              />
            </div>
            <CustomerBalancesSection />
          </div>
        </div>
        <RepeatPaymentMethodSave />
        <TerminalCardSave />
        {hasEmail ? <EmailReceiptToggle /> : <AddEmailToCustomer />}
      </>
    )
  }

  return (
    <>
      <div className="vd-mt10">
        <CustomerSearch
          onSelect={addCustomerToSale}
          onAdd={query => {
            setCurrentQuery(query || '')
            setOpenAddCustomerModal(true)
          }}
          onFetch={setCurrentQuery}
          placeholder={
            isTerminalCardSaveEligible
              ? t('Add a customer to email receipt or save card', {
                  _context:
                    'placeholder for customer select on the sale confirmation when card save is available',
                })
              : t('Add a customer to email them a receipt', {
                  _context:
                    'placeholder for customer select on the sale confirmation',
                })
          }
        />
      </div>
      {openAddCustomerModal && (
        <UpdateSaleCustomerModal
          onClose={() => {
            setOpenAddCustomerModal(false)
          }}
          customerSearchQuery={currentQuery}
        />
      )}
    </>
  )
}
